import { FC, useState } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './IdeaStopDetails.module.scss';
import { IdeaStop } from '../../../../../types/ideaStop.types';
import { IdeaStopListItem } from '../../../../../types/idea.types';
import IdeaStopInfo from './components/IdeaStopInfo/IdeaStopInfo';
import CustomButton from '../../../../../common/CustomButton';
import {
  useDeleteIdeaStopCustomCoverMutation,
  useUploadIdeaStopCustomCoverMutation,
} from '../../../../../store/services/ideasApi';
import { EditIcon } from '../../../../../components/Icons';
import CoverUploadModal from '../../../../../common/CoverUploadModal/CoverUploadModal';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';
import ActivityHeader from '../../../TripDetails/components/StopDetails/components/ActivityHeader/ActivityHeader';

import ideaStopCoverFallback from '../../../../../assets/images/TripMockDetails.jpg';

type PendingConfirm = null | 'cover';

const ideaStopToListItem = (stop: IdeaStop): IdeaStopListItem => ({
  id: stop.id,
  title: (typeof stop.title === 'string' && stop.title.trim()) || stop.name || 'Idea Stop',
  stop_type: stop.stop_type,
  description: stop.description,
  custom_cover: stop.custom_cover ?? undefined,
  start_date_time: stop.start_date_time ?? undefined,
  end_date_time: stop.end_date_time ?? undefined,
  name: stop.name ?? undefined,
  place: stop.place ?? undefined,
});

interface IdeaStopDetailsProps {
  ideaStop: IdeaStop;
  onClose: () => void;
  onRefetchIdea?: () => void;
  ideaId?: number;
  onEditRequest?: (ideaStop: IdeaStopListItem) => void;
  onDeleteRequest?: (ideaStop: IdeaStopListItem) => void;
  nestedModalZIndex?: number;
}

const IdeaStopDetails: FC<IdeaStopDetailsProps> = ({
  ideaStop,
  onClose,
  onRefetchIdea,
  ideaId,
  onEditRequest,
  onDeleteRequest,
  nestedModalZIndex,
}) => {
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const [uploadStopCover] = useUploadIdeaStopCustomCoverMutation();
  const [deleteStopCover, { isLoading: isDeletingCover }] = useDeleteIdeaStopCustomCoverMutation();
  const coverUrl = ideaStop.custom_cover;

  const handleEdit = () => {
    onEditRequest?.(ideaStopToListItem(ideaStop));
  };

  const handleDeleteClick = () => {
    onDeleteRequest?.(ideaStopToListItem(ideaStop));
  };

  const performCoverDelete = async () => {
    if (typeof ideaId !== 'number') {
      setPendingConfirm(null);
      return;
    }
    try {
      await deleteStopCover({ ideaStopId: ideaStop.id, ideaId }).unwrap();
      setPendingConfirm(null);
      onRefetchIdea?.();
    } catch (error) {
      console.error('Failed to delete idea stop cover:', error);
    }
  };

  return (
    <>
      <Box className={styles.ideaStopDetails}>
        <ActivityHeader
          onClose={onClose}
          title="Idea Details"
        />

        <Box className={styles.coverHero}>
          <Box
            className={styles.coverHeroBg}
            sx={{
              backgroundImage: `url(${coverUrl || ideaStopCoverFallback})`,
            }}
          />
        </Box>

        <Box className={styles.content}>
          <Box className={styles.activityTypeRow}>
            <Typography
              variant="h9Bold"
              sx={{ color: '#222B3A' }}
            >
              {ideaStop.stop_type}
            </Typography>
            <CustomButton
              variant="outlined"
              onClick={handleEdit}
              customClass={styles.editButton}
              startIcon={<EditIcon size={20} />}
              textVariant="h11Bold"
            >
              Edit
            </CustomButton>
          </Box>

          <IdeaStopInfo ideaStop={ideaStop} />

          <Box className={styles.actionsSection}>
            <CustomButton
              variant="outlined"
              onClick={handleDeleteClick}
              customClass={styles.deleteButton}
            >
              Delete
            </CustomButton>
          </Box>
        </Box>
      </Box>
      {typeof ideaId === 'number' ? (
        <CoverUploadModal
          open={showCoverModal}
          onClose={() => setShowCoverModal(false)}
          zIndex={nestedModalZIndex}
          title="Update Idea Stop Cover"
          description="Choose a new image for this stop cover."
          placeholder={{
            title: 'Select your stop cover',
            subtitle: '(e.g., PNG, JPG, WEBP up to 5MB)',
          }}
          uploadFile={file =>
            uploadStopCover({ ideaStopId: ideaStop.id, file, ideaId })
              .unwrap()
              .then(() => {
                onRefetchIdea?.();
              })
          }
        />
      ) : null}

      <ConfirmActionModal
        open={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={performCoverDelete}
        description="Do you really want to delete the cover image?"
        confirmLabel="Delete"
        isConfirming={isDeletingCover}
        zIndex={nestedModalZIndex}
      />
    </>
  );
};

export default IdeaStopDetails;
